// Human-readable times for chat.
//
// Every message already carries a createdAt — ChatMessage records one and the
// API returns the whole row — and every session an updatedAt. None of it
// reached the screen: the context mapped messages down to id/role/content/tools
// and dropped the timestamp, and the session list rendered only a title. So a
// conversation had no sense of when anything happened, and the sidebar could
// not tell you whether a chat was from an hour ago or last month.

// One formatter per set of options, made once. toLocaleString and its
// siblings build a new Intl.DateTimeFormat on every call, which is slow: the
// navigation rail stamps each of its chats with exactTime, and while a reply
// streamed in, that alone was a fifth of the browser's busy time (2026-10-31,
// e2e/speed.mjs). The words are the same; Intl is asked once.
const formatters = new Map();
const formatter = (options) => {
  const key = JSON.stringify(options);
  let f = formatters.get(key);
  if (!f) { f = new Intl.DateTimeFormat(undefined, options); formatters.set(key, f); }
  return f;
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * A short, glanceable age: "now", "4m", "3h", "Yesterday", "12 Aug".
 *
 * Deliberately terse — this sits beside a title or under a message, where a
 * full date would compete with the content it is annotating.
 */
export function relativeTime(value, now = Date.now()) {
  const then = toMillis(value);
  if (then === null) return '';

  const diff = now - then;

  // Clock skew, or a message stamped a moment in the future by a server whose
  // time differs slightly. Reading "in 3 seconds" is worse than "now".
  if (diff < MINUTE) return 'now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h`;

  const d = new Date(then);
  const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0);
  const daysBack = Math.floor((startOfToday - new Date(d).setHours(0, 0, 0, 0)) / DAY);

  if (daysBack === 1) return 'Yesterday';
  if (daysBack < 7) return `${daysBack}d`;

  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return formatter({
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d);
}

/**
 * The full stamp, for a tooltip. The short form answers "roughly when"; this
 * answers "exactly when" without spending any space until asked.
 */
export function exactTime(value) {
  const then = toMillis(value);
  if (then === null) return '';
  return formatter({ dateStyle: 'medium', timeStyle: 'short' }).format(new Date(then));
}

// Accepts an ISO string from the API, a Date, or a millisecond number — live
// messages are stamped client-side and persisted ones arrive as strings.
function toMillis(value) {
  if (value === null || value === undefined) return null;
  const ms = value instanceof Date ? value.getTime()
    : typeof value === 'number' ? value
    : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}
